import type { ChangeEvent } from 'react'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import { TextInput } from '../../inputs'
import { InputVariant } from '../../inputs/base'
import { Field } from '../field'
import { useFieldInputAria } from '../hooks'
import type { FieldStatus } from '../types'
import type { TextFieldProps } from './types'

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(({ onChange, ...props }, ref) => {
  const [internalValue, setInternalValue] = useState(props.value)
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(event)
      setInternalValue(event.target.value)
    },
    [onChange]
  )

  useEffect(() => {
    setInternalValue(props.value)
  }, [props.value])

  const variant = props.error ? InputVariant.critical : InputVariant.neutral
  const { 'aria-labelledby': ariaLabelledBy, error, label, helperLabel } = props
  const { fieldElementsIds, fieldInputAriaAttributes } = useFieldInputAria({
    ariaLabelledBy,
    error,
    label,
    helperLabel,
  })
  return (
    <Field
      {...props}
      {...fieldElementsIds}
      counter={internalValue?.length ?? 0}
      status={variant as unknown as FieldStatus}
    >
      <TextInput
        {...props}
        {...fieldInputAriaAttributes}
        onChange={handleChange}
        ref={ref}
        value={internalValue}
        variant={variant}
      />
    </Field>
  )
})
TextField.displayName = 'TextField'
export default TextField
